import { useState, useCallback, useMemo, useRef, useEffect, Component, type ReactNode } from "react";
import { Highlight, themes, type Language } from "prism-react-renderer";
import { useTheme } from "next-themes";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  Trash2,
  Bold,
  Italic,
  Code,
  Link as LinkIcon,
  Pencil,
  Check,
  GitCommit,
  X,
  GripVertical,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CodeEditor } from "@/components/ui/code-editor";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { useCommitMutations, useUserCommits } from "../../hooks/useCommits";
import { useMe } from "../../hooks/useUser";
import TypeDropdown from "./TypeDropdown";
import BranchCombobox from "./BranchCombobox";
import CommitCard from "./CommitCard";
import type {
  CreateCommitInput,
  UpdateCommitInput,
  CommitType,
  Commit,
  TakeawayItem,
  TakeawayItemType,
} from "../../types/api";

// Takeaway item configuration
const TAKEAWAY_CONFIG: Record<
  TakeawayItemType,
  { symbol: string; color: string; label: string }
> = {
  do: { symbol: "👍", color: "text-green-500", label: "Do" },
  dont: { symbol: "👎", color: "text-red-500", label: "Don't" },
  idea: { symbol: "💡", color: "text-amber-500", label: "Idea" },
};

// Character limits
const LIMITS = {
  type: 10,
  message: 128,
  body: 512,
  changeItem: 64,
  maxChanges: 5,
  diffMaxLines: 32,
  diffDisplayLinesDiff: 5,
  diffDisplayLinesCode: 10,
  diffMaxChars: 1024,
} as const;

// Languages supported by prism-react-renderer's default bundle
const CODE_LANGUAGES = [
  { value: "text", label: "Plain Text" },
  { value: "javascript", label: "JavaScript" },
  { value: "typescript", label: "TypeScript" },
  { value: "jsx", label: "JSX" },
  { value: "tsx", label: "TSX" },
  { value: "python", label: "Python" },
  { value: "go", label: "Go" },
  { value: "rust", label: "Rust" },
  { value: "cpp", label: "C++" },
  { value: "c", label: "C" },
  { value: "swift", label: "Swift" },
  { value: "kotlin", label: "Kotlin" },
  { value: "objectivec", label: "Objective-C" },
  { value: "html", label: "HTML" },
  { value: "css", label: "CSS" },
  { value: "sql", label: "SQL" },
  { value: "json", label: "JSON" },
  { value: "yaml", label: "YAML" },
  { value: "markdown", label: "Markdown" },
  { value: "graphql", label: "GraphQL" },
] as const;

// Map language values to Prism language names
const PRISM_LANGUAGE_MAP: Record<string, Language | undefined> = {
  text: undefined,
  javascript: "javascript",
  typescript: "typescript",
  python: "python",
  go: "go",
  rust: "rust",
  cpp: "cpp",
  c: "c",
  swift: "swift",
  kotlin: "kotlin",
  html: "markup",
  css: "css",
  sql: "sql",
  json: "json",
  yaml: "yaml",
  jsx: "jsx",
  tsx: "tsx",
  markdown: "markdown",
  graphql: "graphql",
  objectivec: "objectivec",
};

// Error boundary for syntax highlighting
class SyntaxHighlightBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; fallback: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// Sortable takeaway item component (defined outside to prevent re-creation on each render)
interface SortableTakeawayItemProps {
  item: TakeawayItem;
  index: number;
  editingTakeaway: { index: number; value: string } | null;
  onEditTakeaway: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onStartEdit: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

function SortableTakeawayItem({
  item,
  index,
  editingTakeaway,
  onEditTakeaway,
  onSaveEdit,
  onCancelEdit,
  onStartEdit,
  onRemove,
}: SortableTakeawayItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `takeaway-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isEditing = editingTakeaway?.index === index;
  const config = TAKEAWAY_CONFIG[item.type];

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span className={config.color}>{config.symbol}</span>
        <Input
          value={editingTakeaway.value}
          onChange={(e) => onEditTakeaway(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSaveEdit();
            } else if (e.key === "Escape") {
              onCancelEdit();
            }
          }}
          className="flex-1 h-8 text-sm"
          maxLength={LIMITS.changeItem}
          autoFocus
        />
        <button
          type="button"
          onClick={onSaveEdit}
          className="text-[hsl(var(--success))] hover:text-[hsl(var(--success))]/80"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onCancelEdit}
          className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 group ${
        isDragging ? "opacity-50" : ""
      }`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className={config.color}>{config.symbol}</span>
      <span className="flex-1 text-sm">{item.text}</span>
      <div className="flex items-center gap-3 sm:gap-1">
        <button
          type="button"
          onClick={() => onStartEdit(index, item.text)}
          className="p-2 -m-2 sm:p-0 sm:m-0 sm:opacity-0 sm:group-hover:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-opacity"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-2 -m-2 sm:p-0 sm:m-0 sm:opacity-0 sm:group-hover:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] transition-opacity"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const formSchema = z.object({
  type: z
    .string()
    .min(1, "Type is required")
    .max(LIMITS.type, `Type must be ${LIMITS.type} characters or less`),
  branch: z.string().min(1),
  message: z
    .string()
    .min(1, "Commit message is required")
    .max(
      LIMITS.message,
      `Message must be ${LIMITS.message} characters or less`
    ),
  body: z
    .string()
    .max(LIMITS.body, `Description must be ${LIMITS.body} characters or less`)
    .optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CommitEditorProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Pass an existing commit to enable edit mode */
  commit?: Commit;
}

interface EditingTakeaway {
  index: number;
  value: string;
}

export default function CommitEditor({
  open,
  onClose,
  onSuccess,
  commit: existingCommit,
}: CommitEditorProps) {
  const isEditMode = !!existingCommit;
  const { theme } = useTheme();
  const isDark =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const [takeawayItems, setTakeawayItems] = useState<TakeawayItem[]>([]);
  const [newTakeaway, setNewTakeaway] = useState("");
  const [takeawayType, setTakeawayType] = useState<TakeawayItemType>("do");

  // dnd-kit sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [editingTakeaway, setEditingTakeaway] = useState<EditingTakeaway | null>(
    null
  );
  const [snippetBefore, setSnippetBefore] = useState("");
  const [snippetAfter, setSnippetAfter] = useState("");
  const [snippetLanguage, setSnippetLanguage] = useState("text");
  const [snippetModalOpen, setSnippetModalOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const { createCommit, updateCommit, loading } = useCommitMutations();
  const { user } = useMe();

  // Only fetch user commits when modal is open (to get branch suggestions)
  // This prevents unnecessary requests when viewing other pages
  const shouldFetchCommits = open && !!user?.id;
  const { commits: userCommits } = useUserCommits(
    shouldFetchCommits ? user.id : "",
    50
  );

  // Extract unique branches from user's commits (excluding 'main')
  const userBranches = useMemo(() => {
    if (!userCommits || !shouldFetchCommits) return [];
    const branches = [...new Set(userCommits.map((c) => c.branch || "main"))];
    return branches.filter((b) => b !== "main");
  }, [userCommits, shouldFetchCommits]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: "feat",
      branch: "main",
      message: "",
      body: "",
    },
  });

  // Watch form values for live preview
  const watchedValues = useWatch({ control: form.control });
  const previewCommit: Commit = useMemo(
    () => ({
      id: "preview",
      userId: user?.id || "preview",
      type: (watchedValues.type as CommitType) || "feat",
      branch: watchedValues.branch || "main",
      message: watchedValues.message || "Your commit message...",
      body: watchedValues.body || undefined,
      tags,
      takeaways: takeawayItems.length > 0 ? { items: takeawayItems } : undefined,
      snippet: snippetBefore
        ? {
            before: snippetBefore,
            after: snippetAfter || undefined,
            language: snippetLanguage || undefined,
          }
        : undefined,
      user: user || undefined,
      reactionCounts: {
        heart: 0,
        celebration: 0,
        fire: 0,
        sleep: 0,
        dynamite: 0,
      },
      commentCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }),
    [
      watchedValues,
      tags,
      takeawayItems,
      snippetBefore,
      snippetAfter,
      snippetLanguage,
      user,
    ]
  );

  // Initialize form and state when editing or when modal opens
  useEffect(() => {
    if (open) {
      if (existingCommit) {
        // Edit mode: populate from existing commit
        form.reset({
          type: existingCommit.type || "feat",
          branch: existingCommit.branch || "main",
          message: existingCommit.message || "",
          body: existingCommit.body || "",
        });
        setTakeawayItems(existingCommit.takeaways?.items || []);
        setTags(existingCommit.tags || []);
        setSnippetBefore(existingCommit.snippet?.before || "");
        setSnippetAfter(existingCommit.snippet?.after || "");
        setSnippetLanguage(existingCommit.snippet?.language || "text");
      } else {
        // Create mode: reset to defaults
        form.reset({
          type: "feat",
          branch: "main",
          message: "",
          body: "",
        });
        setTakeawayItems([]);
        setTags([]);
        setSnippetBefore("");
        setSnippetAfter("");
        setSnippetLanguage("text");
      }
      setEditingTakeaway(null);
    }
  }, [open, existingCommit, form]);

  const handleAddTakeaway = useCallback(() => {
    if (!newTakeaway.trim()) return;
    if (takeawayItems.length >= LIMITS.maxChanges) {
      toast.error(`Maximum ${LIMITS.maxChanges} tips allowed`);
      return;
    }
    const trimmed = newTakeaway.trim().slice(0, LIMITS.changeItem);
    setTakeawayItems((prev) => [...prev, { type: takeawayType, text: trimmed }]);
    setNewTakeaway("");
  }, [newTakeaway, takeawayType, takeawayItems.length]);

  const handleRemoveTakeaway = useCallback((index: number) => {
    setTakeawayItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleStartEditTakeaway = useCallback((index: number, value: string) => {
    setEditingTakeaway({ index, value });
  }, []);

  const handleSaveEditTakeaway = useCallback(() => {
    if (!editingTakeaway) return;
    const { index, value } = editingTakeaway;
    if (value.trim()) {
      const trimmed = value.trim().slice(0, LIMITS.changeItem);
      setTakeawayItems((prev) =>
        prev.map((item, i) => (i === index ? { ...item, text: trimmed } : item))
      );
    }
    setEditingTakeaway(null);
  }, [editingTakeaway]);

  const handleCancelEditTakeaway = useCallback(() => {
    setEditingTakeaway(null);
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setTakeawayItems((items) => {
        const oldIndex = items.findIndex((_, i) => `takeaway-${i}` === active.id);
        const newIndex = items.findIndex((_, i) => `takeaway-${i}` === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  const handleAddTag = useCallback(() => {
    if (!tagInput.trim()) return;
    // Remove # and spaces from tag
    const tag = tagInput.trim().replace(/^#/, "").replace(/\s+/g, "");
    if (!tag) return;
    // Case-insensitive duplicate check
    if (!tags.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setTags((prev) => [...prev, tag]);
    }
    setTagInput("");
  }, [tagInput, tags]);

  const handleRemoveTag = useCallback((tagToRemove: string) => {
    setTags((prev) => prev.filter((t) => t !== tagToRemove));
  }, []);

  const handleSubmit = async (values: FormValues) => {
    try {
      const takeawaysData =
        takeawayItems.length > 0 ? { items: takeawayItems } : undefined;

      const snippetValue = snippetBefore
        ? {
            before: snippetBefore,
            after: snippetAfter || undefined,
            language: snippetLanguage !== "text" ? snippetLanguage : undefined,
          }
        : undefined;

      if (isEditMode && existingCommit) {
        // Update existing commit
        // Use null to explicitly remove diff if it was cleared
        const input: UpdateCommitInput = {
          type: values.type as CommitType,
          branch: values.branch,
          message: values.message,
          body: values.body || undefined,
          tags,
          takeaways: takeawaysData,
          snippet: snippetValue === undefined ? (null as unknown as undefined) : snippetValue,
        };

        await updateCommit(existingCommit.id, input);
        toast.success("Commit updated!");
      } else {
        // Create new commit
        const input: CreateCommitInput = {
          type: values.type as CommitType,
          branch: values.branch,
          message: values.message,
          body: values.body || undefined,
          tags,
          takeaways: takeawaysData,
          snippet: snippetValue,
        };

        await createCommit(input);
        toast.success("Commit created!");
      }

      form.reset();
      setTakeawayItems([]);
      setTags([]);
      setSnippetBefore("");
      setSnippetAfter("");
      setSnippetLanguage("text");
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(
        isEditMode ? "Failed to update commit" : "Failed to create commit"
      );
      console.error(error);
    }
  };

  const insertMarkdown = (prefix: string, suffix: string = prefix) => {
    const textarea =
      document.querySelector<HTMLTextAreaElement>("#commit-body");
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const selectedText = text.substring(start, end);

    const newText =
      text.substring(0, start) +
      prefix +
      selectedText +
      suffix +
      text.substring(end);
    form.setValue("body", newText);

    // Move cursor
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd =
        start + prefix.length + selectedText.length + suffix.length;
    }, 0);
  };

  const handleBodyKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMod = e.metaKey || e.ctrlKey;
    if (!isMod) return;

    switch (e.key.toLowerCase()) {
      case "b":
        e.preventDefault();
        insertMarkdown("**");
        break;
      case "i":
        e.preventDefault();
        insertMarkdown("_");
        break;
      case "k":
        e.preventDefault();
        insertMarkdown("[", "](url)");
        break;
      case "`":
        e.preventDefault();
        insertMarkdown("`");
        break;
    }
  };

  // Handler for editing change value
  const handleEditTakeawayValue = useCallback((value: string) => {
    setEditingTakeaway((prev) => (prev ? { ...prev, value } : null));
  }, []);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogPortal>
        <DialogOverlay />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4">
          <div className="flex gap-6 h-full sm:h-auto sm:max-h-[90vh] w-full sm:max-w-[550px] lg:max-w-[1100px]">
            {/* Form Card - using DialogPrimitive.Content for proper touch handling */}
            <DialogPrimitive.Content
              className="relative flex-1 lg:flex-[0_0_550px] bg-[hsl(var(--background))] sm:border border-[hsl(var(--border))] rounded-none sm:rounded-lg sm:shadow-lg flex flex-col h-full sm:h-auto sm:max-h-[90vh] outline-none"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => {
                // Prevent closing when clicking the preview panel
                const target = e.target as HTMLElement;
                if (target.closest("[data-preview-panel]")) {
                  e.preventDefault();
                }
              }}
              onInteractOutside={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest("[data-preview-panel]")) {
                  e.preventDefault();
                }
              }}
            >
              <DialogPrimitive.Close className="absolute right-4 top-4 h-8 w-8 flex items-center justify-center rounded-md opacity-70 ring-offset-[hsl(var(--background))] transition-opacity hover:opacity-100 hover:bg-[hsl(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:ring-offset-2 disabled:pointer-events-none">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
              <div className="p-4 sm:p-6 pb-4 flex-shrink-0 border-b border-[hsl(var(--border))]">
                <DialogTitle className="text-lg font-semibold font-mono flex items-center gap-2">
                  {isEditMode ? (
                    <>
                      <Pencil className="h-5 w-5" />
                      Edit Commit
                    </>
                  ) : (
                    <>
                      <GitCommit className="h-5 w-5" />
                      New Commit
                    </>
                  )}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {isEditMode ? "Edit your commit details" : "Create a new commit to track your progress"}
                </DialogDescription>
              </div>

              <div
                ref={scrollRef}
                className="px-4 sm:px-6 py-6 overflow-y-auto flex-1"
              >
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(handleSubmit)}
                    className="space-y-6"
                  >
                    {/* Type + Commit message */}
                    <div className="space-y-2">
                      <FormLabel>Commit Message</FormLabel>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                        <div className="flex items-center flex-shrink-0">
                          <FormField
                            control={form.control}
                            name="type"
                            render={({ field }) => (
                              <FormItem className="flex-shrink-0">
                                <FormControl>
                                  <TypeDropdown
                                    value={field.value}
                                    onChange={field.onChange}
                                    className="sm:rounded-r-none sm:border-r-0"
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <span className="hidden sm:block text-[hsl(var(--muted-foreground))] font-mono text-lg">
                            :
                          </span>
                        </div>
                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field, fieldState }) => (
                            <FormItem className="flex-1">
                              <FormControl>
                                <Input
                                  placeholder="Your commit message..."
                                  className={`font-mono ${
                                    fieldState.error
                                      ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]"
                                      : ""
                                  }`}
                                  maxLength={LIMITS.message}
                                  {...field}
                                />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Branch */}
                    <FormField
                      control={form.control}
                      name="branch"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Branch</FormLabel>
                          <FormControl>
                            <BranchCombobox
                              value={field.value}
                              onChange={field.onChange}
                              userBranches={userBranches}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    {/* Body with markdown toolbar */}
                    <FormField
                      control={form.control}
                      name="body"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description (optional)</FormLabel>
                          <div className="space-y-2">
                            <div className="flex gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => insertMarkdown("**")}
                              >
                                <Bold className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => insertMarkdown("_")}
                              >
                                <Italic className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => insertMarkdown("`")}
                              >
                                <Code className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => insertMarkdown("[", "](url)")}
                              >
                                <LinkIcon className="h-4 w-4" />
                              </Button>
                            </div>
                            <FormControl>
                              <Textarea
                                id="commit-body"
                                placeholder="Add more details... (Markdown supported)"
                                className="font-mono text-sm min-h-[100px]"
                                maxLength={LIMITS.body}
                                onKeyDown={handleBodyKeyDown}
                                {...field}
                              />
                            </FormControl>
                          </div>
                        </FormItem>
                      )}
                    />

                    <Separator />

                    {/* Code Snippet section */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Code Snippet</h4>
                        {snippetBefore && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSnippetBefore("");
                              setSnippetAfter("");
                            }}
                            className="h-6 px-2 text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))]"
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Remove
                          </Button>
                        )}
                      </div>
                      {snippetBefore ? (
                        <button
                          type="button"
                          onClick={() => setSnippetModalOpen(true)}
                          className="w-full text-left p-3 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/50 hover:bg-[hsl(var(--muted))] transition-colors overflow-hidden"
                        >
                          {(() => {
                            const previewCode = snippetBefore.length > 100
                              ? snippetBefore.slice(0, 100) + "..."
                              : snippetBefore;
                            const prismLang = PRISM_LANGUAGE_MAP[snippetLanguage];
                            const fallback = (
                              <pre className="font-mono text-sm text-[hsl(var(--foreground))] whitespace-pre-wrap overflow-hidden">
                                {previewCode}
                              </pre>
                            );
                            if (!prismLang) return fallback;
                            return (
                              <SyntaxHighlightBoundary fallback={fallback}>
                                <Highlight
                                  theme={isDark ? themes.nightOwl : themes.github}
                                  code={previewCode}
                                  language={prismLang}
                                >
                                  {({ style, tokens, getLineProps, getTokenProps }) => (
                                    <pre
                                      className="font-mono text-sm whitespace-pre-wrap overflow-hidden"
                                      style={{ ...style, background: "transparent", margin: 0 }}
                                    >
                                      {tokens.map((line, i) => (
                                        <div key={i} {...getLineProps({ line })}>
                                          {line.map((token, key) => (
                                            <span key={key} {...getTokenProps({ token })} />
                                          ))}
                                        </div>
                                      ))}
                                    </pre>
                                  )}
                                </Highlight>
                              </SyntaxHighlightBoundary>
                            );
                          })()}
                          {snippetAfter && (
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">
                              + diff attached
                            </p>
                          )}
                        </button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSnippetModalOpen(true)}
                          className="w-full"
                        >
                          <Code className="h-4 w-4 mr-2" />
                          Add Code Snippet
                        </Button>
                      )}
                    </div>

                    <Separator />

                    {/* Tips section */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Takeaways</h4>

                      {/* Add change input */}
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Select
                          value={takeawayType}
                          onValueChange={(value) =>
                            setTakeawayType(value as TakeawayItemType)
                          }
                        >
                          <SelectTrigger className="w-full sm:w-[160px] flex-shrink-0">
                            <SelectValue>
                              <span className="flex items-center gap-2 whitespace-nowrap">
                                <span
                                  className={TAKEAWAY_CONFIG[takeawayType].color}
                                >
                                  {TAKEAWAY_CONFIG[takeawayType].symbol}
                                </span>
                                <span>{TAKEAWAY_CONFIG[takeawayType].label}</span>
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="do">
                              <span className="flex items-center gap-2">
                                <span className="text-green-500">👍</span>
                                <span>Do</span>
                              </span>
                            </SelectItem>
                            <SelectItem value="dont">
                              <span className="flex items-center gap-2">
                                <span className="text-red-500">👎</span>
                                <span>Don't</span>
                              </span>
                            </SelectItem>
                            <SelectItem value="idea">
                              <span className="flex items-center gap-2">
                                <span className="text-amber-500">💡</span>
                                <span>Idea</span>
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex gap-2 flex-1">
                          <Input
                            value={newTakeaway}
                            onChange={(e) => setNewTakeaway(e.target.value)}
                            onKeyDown={(e) =>
                              e.key === "Enter" &&
                              (e.preventDefault(), handleAddTakeaway())
                            }
                            placeholder="What changed?"
                            className="flex-1"
                            maxLength={LIMITS.changeItem}
                            disabled={takeawayItems.length >= LIMITS.maxChanges}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={handleAddTakeaway}
                            className="flex-shrink-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Changes list with drag-and-drop */}
                      {takeawayItems.length > 0 && (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={takeawayItems.map((_, i) => `takeaway-${i}`)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="bg-[hsl(var(--muted))] rounded-lg p-3 space-y-1">
                              {takeawayItems.map((item, i) => (
                                <SortableTakeawayItem
                                  key={`takeaway-${i}`}
                                  item={item}
                                  index={i}
                                  editingTakeaway={editingTakeaway}
                                  onEditTakeaway={handleEditTakeawayValue}
                                  onSaveEdit={handleSaveEditTakeaway}
                                  onCancelEdit={handleCancelEditTakeaway}
                                  onStartEdit={handleStartEditTakeaway}
                                  onRemove={handleRemoveTakeaway}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>

                    {/* Code Snippet Modal */}
                    <Dialog
                      open={snippetModalOpen}
                      onOpenChange={setSnippetModalOpen}
                    >
                      <DialogPortal>
                        <DialogOverlay />
                        <DialogPrimitive.Content
                          className="fixed inset-0 sm:inset-auto sm:left-[50%] sm:top-[50%] z-50 w-full sm:max-w-[800px] h-full sm:h-auto sm:max-h-[90vh] sm:translate-x-[-50%] sm:translate-y-[-50%] bg-[hsl(var(--background))] sm:border border-[hsl(var(--border))] rounded-none sm:rounded-lg sm:shadow-lg outline-none flex flex-col"
                          onOpenAutoFocus={(e) => e.preventDefault()}
                        >
                          <div className="p-4 sm:p-6 pb-0 flex-shrink-0">
                            <DialogPrimitive.Close className="absolute right-4 top-4 h-8 w-8 flex items-center justify-center rounded-md opacity-70 ring-offset-[hsl(var(--background))] transition-opacity hover:opacity-100 hover:bg-[hsl(var(--muted))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))] focus:ring-offset-2">
                              <X className="h-4 w-4" />
                              <span className="sr-only">Close</span>
                            </DialogPrimitive.Close>
                            <DialogTitle className="text-lg font-semibold mb-4">
                              Code Snippet
                            </DialogTitle>
                            <DialogDescription className="sr-only">
                              Add a code snippet or diff to your commit
                            </DialogDescription>
                          </div>
                          <div className="px-4 sm:px-6 pb-6 overflow-y-auto flex-1 space-y-4">
                            {/* Language selector */}
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Language
                              </label>
                              <Select
                                value={snippetLanguage}
                                onValueChange={setSnippetLanguage}
                              >
                                <SelectTrigger className="w-full sm:w-[200px]">
                                  <SelectValue placeholder="Select language" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CODE_LANGUAGES.map((lang) => (
                                    <SelectItem
                                      key={lang.value}
                                      value={lang.value}
                                    >
                                      {lang.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-medium">
                                Code
                              </label>
                              <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] overflow-hidden">
                                <CodeEditor
                                  value={snippetBefore}
                                  onChange={(value) => {
                                    const lines = value.split("\n");
                                    if (
                                      lines.length <= LIMITS.diffMaxLines &&
                                      value.length <= LIMITS.diffMaxChars
                                    ) {
                                      setSnippetBefore(value);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Tab") {
                                      e.preventDefault();
                                      const target =
                                        e.target as HTMLTextAreaElement;
                                      const start = target.selectionStart;
                                      const end = target.selectionEnd;
                                      const newValue =
                                        snippetBefore.substring(0, start) +
                                        "  " +
                                        snippetBefore.substring(end);
                                      if (
                                        newValue.length <= LIMITS.diffMaxChars
                                      ) {
                                        setSnippetBefore(newValue);
                                        setTimeout(() => {
                                          target.selectionStart =
                                            target.selectionEnd = start + 2;
                                        }, 0);
                                      }
                                    }
                                  }}
                                  language={snippetLanguage}
                                  placeholder="const x = 1;"
                                  className="min-h-[150px]"
                                  autoFocus
                                />
                              </div>
                              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                {snippetBefore.length}/{LIMITS.diffMaxChars} chars,{" "}
                                {snippetBefore.split("\n").filter(Boolean).length}/
                                {LIMITS.diffMaxLines} lines
                              </p>
                              {/* Truncation feedback */}
                              {(() => {
                                const beforeLines =
                                  snippetBefore.split("\n").length;
                                const displayLimit = snippetAfter
                                  ? LIMITS.diffDisplayLinesDiff
                                  : LIMITS.diffDisplayLinesCode;
                                const hiddenLines = beforeLines - displayLimit;
                                if (hiddenLines > 0) {
                                  return (
                                    <p className="text-xs text-amber-500">
                                      {hiddenLines} line
                                      {hiddenLines > 1 ? "s" : ""} will be
                                      hidden when displayed
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                            <details className="group">
                              <summary className="text-sm font-medium cursor-pointer text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors list-none flex items-center gap-1">
                                <span className="text-xs transition-transform group-open:rotate-90">
                                  ▶
                                </span>
                                Show diff (before → after)
                              </summary>
                              <div className="space-y-2 mt-3">
                                <label className="text-sm font-medium">
                                  After
                                </label>
                                <div className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--background))] overflow-hidden">
                                  <CodeEditor
                                    value={snippetAfter}
                                    onChange={(value) => {
                                      const lines = value.split("\n");
                                      if (
                                        lines.length <= LIMITS.diffMaxLines &&
                                        value.length <= LIMITS.diffMaxChars
                                      ) {
                                        setSnippetAfter(value);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Tab") {
                                        e.preventDefault();
                                        const target =
                                          e.target as HTMLTextAreaElement;
                                        const start = target.selectionStart;
                                        const end = target.selectionEnd;
                                        const newValue =
                                          snippetAfter.substring(0, start) +
                                          "  " +
                                          snippetAfter.substring(end);
                                        if (
                                          newValue.length <= LIMITS.diffMaxChars
                                        ) {
                                          setSnippetAfter(newValue);
                                          setTimeout(() => {
                                            target.selectionStart =
                                              target.selectionEnd = start + 2;
                                          }, 0);
                                        }
                                      }
                                    }}
                                    language={snippetLanguage}
                                    placeholder="const x = 2;"
                                    className="min-h-[150px]"
                                  />
                                </div>
                                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                                  {snippetAfter.length}/{LIMITS.diffMaxChars}{" "}
                                  chars,{" "}
                                  {snippetAfter.split("\n").filter(Boolean).length}
                                  /{LIMITS.diffMaxLines} lines
                                </p>
                              </div>
                            </details>
                            <div className="flex justify-end pt-2">
                              <Button
                                type="button"
                                onClick={() => setSnippetModalOpen(false)}
                              >
                                Done
                              </Button>
                            </div>
                          </div>
                        </DialogPrimitive.Content>
                      </DialogPortal>
                    </Dialog>

                    <Separator />

                    {/* Tags */}
                    <div className="space-y-4">
                      <h4 className="font-medium">Tags</h4>
                      <div className="flex gap-2">
                        <Input
                          value={tagInput}
                          onChange={(e) =>
                            setTagInput(
                              e.target.value
                                .replace(/\s+/g, "-")              // Replace spaces with dashes
                                .replace(/[^a-zA-Z0-9-]/g, "")     // Only allow alphanumeric and dashes
                                .replace(/-+/g, "-")               // Collapse multiple dashes
                                .replace(/^-/, "")                 // Remove leading dash
                            )
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleAddTag();
                            }
                          }}
                          placeholder="react, typescript"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={handleAddTag}
                          className="flex-shrink-0"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                      {tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {tags.map((tag) => (
                            <Badge
                              key={tag}
                              variant="secondary"
                              className="font-mono gap-1 pr-1"
                            >
                              #{tag}
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="ml-1 hover:text-[hsl(var(--destructive))]"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-[hsl(var(--border))]">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        className="w-full sm:w-auto"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        loading={loading}
                        className="w-full sm:w-auto"
                      >
                        {isEditMode ? "Save Changes" : "Commit"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </div>
            </DialogPrimitive.Content>

            {/* Preview Panel - hidden on mobile/tablet */}
            <div
              className="hidden lg:block flex-1 min-w-[400px]"
              data-preview-panel
            >
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 font-medium">
                Preview
              </p>
              <div
                className="overflow-y-scroll overflow-x-hidden max-h-[calc(90vh-3rem)] pb-4 pr-3 pointer-events-auto"
                onWheel={(e) => e.stopPropagation()}
              >
                <CommitCard commit={previewCommit} isPreview />
              </div>
            </div>
          </div>
        </div>
      </DialogPortal>
    </Dialog>
  );
}
